import { useQuery } from '@apollo/client'
import { Button, Group, Title } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { Breadcrumbs } from 'components/Breadcrumbs'
import { FullPage404, FullPageError } from 'components/FullPageComponents'
import { FullContentLoader } from 'components/Loading'
import { MessageBox } from 'components/MessageBox'
import { PermissionGate } from 'components/PermissionGate'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PERMISSIONS } from 'util/permissions'
import {
  AddShiftTemplateModal,
  ShiftTemplateAccordion,
} from '../components/ScheduleTemplateDetails'
import { useScheduleTemplateMutations } from '../mutations.hooks'
import { ALL_SCHEDULE_TEMPLATES, SCHEDULE_TEMPLATE_QUERY } from '../queries'
import {
  ScheduleTemplateQueryReturns,
  ScheduleTemplateQueryVariables,
} from '../types.graphql'

const breadcrumbItems = [
  { label: 'Hjem', path: '/dashboard' },
  { label: 'Vaktlister', path: '/schedules' },
  { label: 'Vaktplanmaler', path: '/schedules/templates' },
]

interface ScheduleTemplateDetailsParams {
  templateId: string
}

export const ScheduleTemplateDetails: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const navigate = useNavigate()

  const { templateId } = useParams<
    keyof ScheduleTemplateDetailsParams
  >() as ScheduleTemplateDetailsParams

  const { data, loading, error } = useQuery<
    ScheduleTemplateQueryReturns,
    ScheduleTemplateQueryVariables
  >(SCHEDULE_TEMPLATE_QUERY, { variables: { id: templateId } })

  const { deleteScheduleTemplate } = useScheduleTemplateMutations()

  function handleDeleteScheduleTemplate() {
    const confirmed = window.confirm(
      'Er du sikker på at du vil slette denne vaktplan malen?'
    )
    if (confirmed) {
      deleteScheduleTemplate({
        variables: { id: templateId },
        refetchQueries: [ALL_SCHEDULE_TEMPLATES],
        onCompleted() {
          showNotification({
            title: 'Suksess',
            message: 'Vaktplan malen ble slettet',
            color: 'green',
          })
          navigate('/schedules/templates')
        },
        onError({ message }) {
          showNotification({
            title: 'Noe gikk galt',
            message,
            color: 'red',
          })
        },
      })
    }
  }
  if (error) return <FullPageError />

  if (loading || !data) return <FullContentLoader />

  const { scheduleTemplate } = data

  if (!scheduleTemplate) return <FullPage404 />

  const { shiftTemplates } = scheduleTemplate

  const overloadedItems = [
    ...breadcrumbItems,
    {
      label: `${scheduleTemplate.name} ${scheduleTemplate.schedule.name}`,
      path: '',
    },
  ]

  return (
    <div>
      <Breadcrumbs items={overloadedItems} />
      <Group justify="space-between" my="md">
        <Title>
          {scheduleTemplate.name} {scheduleTemplate.schedule.name}
        </Title>
        <Group>
          <PermissionGate
            permissions={PERMISSIONS.schedules.delete.scheduleTemplate}
          >
            <Button
              leftSection={<IconTrash />}
              color="red"
              onClick={handleDeleteScheduleTemplate}
            >
              Slett mal
            </Button>
          </PermissionGate>
          <Button leftSection={<IconPlus />} onClick={() => setModalOpen(true)}>
            Legg til vakt
          </Button>
        </Group>
      </Group>

      <MessageBox type="info">
        Her kan du definere en mal for en vaktplan. For hver vakt kan du legge
        til en rolle og antall personer som typisk skal være på en slik vakt.
      </MessageBox>
      <ShiftTemplateAccordion shiftTemplates={shiftTemplates} />
      <AddShiftTemplateModal
        open={modalOpen}
        onCloseCallback={() => setModalOpen(false)}
      />
    </div>
  )
}
